import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { getInsightsAnalysis } from '../services/agentApi';
import { ParametersModal } from '../components/ui/ParametersModal';
import {
    ArrowLeft,
    TrendingUp,
    AlertTriangle,
    Lightbulb,
    Target,
    BarChart3,
    Activity,
    DollarSign,
    Users,
    Clock,
    CheckCircle,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Info
} from 'lucide-react';

/**
 * InsightsAnalysisResults - Page for displaying comprehensive analysis results
 * from multi-query execution with detailed business intelligence.
 * 
 * Can be accessed in two ways:
 * 1. Via navigation state (right after execution): /agents/insights/:agentId/results
 *    - location.state.analysisData contains the analysis
 * 2. Via analysis ID (from database): /agents/insights/results/:analysisId
 *    - Fetches analysis from database
 */
export const InsightsAnalysisResults = () => {
    const { t } = useTranslation(['page-insights-results', 'common']);
    const navigate = useNavigate();
    const { agentId, analysisId } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { showToast } = useToast();

    const analysisDataFromState = location.state?.analysisData;
    const fromExecution = location.state?.fromExecution;
    const databaseId = location.state?.databaseId;
    const agentIdFromQuery = searchParams.get('agent_id');

    const [loading, setLoading] = useState(true);
    const [analysisData, setAnalysisData] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [showParamsModal, setShowParamsModal] = useState(false);

    useEffect(() => {
        const loadAnalysis = async () => {
            try {
                if (analysisDataFromState) {
                    // Data passed via navigation state (fresh execution)
                    setAnalysisData(analysisDataFromState);
                } else if (analysisId) {
                    // Load from database by analysis ID
                    const response = await getInsightsAnalysis(analysisId);
                    if (response.data?.status === 'success') {
                        // Transform database format to match execution format
                        const data = response.data.data;
                        setAnalysisData({
                            agent_id: agentIdFromQuery || data.response_data?.agent_id || data.agent_info?.id || agentId,
                            agent_name: data.agent_info?.name,
                            database_id: data.database?.id,
                            insights_executed: data.execution_summary?.insights_executed,
                            queries_executed: data.execution_summary?.queries_executed,
                            successful_queries: data.execution_summary?.successful_queries,
                            failed_queries: data.execution_summary?.failed_queries,
                            comprehensive_analysis: data.comprehensive_analysis,
                            execution_results: data.execution_results,
                            parameters_used: data.parameters_used || {}
                        });
                    } else {
                        showToast(t('toastFailedToLoadAnalysis'), 'error');
                    }
                } else {
                    showToast(t('toastNoAnalysisData'), 'error');
                }
            } catch (error) {
                console.error('Failed to load analysis:', error);
                showToast(t('toastFailedToLoadResults'), 'error');
            } finally {
                setLoading(false);
            }
        };

        loadAnalysis();
    }, [analysisDataFromState, analysisId, agentId, agentIdFromQuery, showToast, t]);

    const handleBack = () => {
        if (fromExecution && databaseId && agentId) {
            // Came from execution - go back to insights selection
            navigate(`/agents/insights/${agentId}`);
        } else {
            // Fallback
            navigate(-1);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('loadingResults')}</p>
                </div>
            </div>
        );
    }

    if (!analysisData) {
        return (
            <div className="max-w-4xl mx-auto p-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-red-900 mb-2">{t('noAnalysisDataTitle')}</h2>
                    <p className="text-red-700 mb-4">{t('noAnalysisDataBody')}</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        {t('goBack')}
                    </button>
                </div>
            </div>
        );
    }

    const {
        comprehensive_analysis,
        queries_executed,
        successful_queries,
        failed_queries,
        insights_executed,
        parameters_used,
        agent_name
    } = analysisData;

    // Flatten parameters from nested structure {query_id: {param: value}} to {param: value}
    const flattenParameters = (params) => {
        if (!params) return {};
        if (typeof params !== 'object') return {};
        
        const flattened = {};
        Object.values(params).forEach(queryParams => {
            if (typeof queryParams === 'object' && queryParams !== null) {
                Object.assign(flattened, queryParams);
            }
        });
        return flattened;
    };

    const flattenedParams = flattenParameters(parameters_used);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const isExpanded = (section) => expandedSections[section] ?? true;

    // Impact badge styling
    const getImpactBadge = (impact) => {
        switch (impact?.toUpperCase()) {
            case 'HIGH':
            case 'CRITICAL':
            case 'P0 (CRITICAL)':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'MEDIUM':
            case 'P1 (HIGH)':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'LOW':
            case 'P2 (MEDIUM)':
            case 'P3 (LOW)':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getDirectionIcon = (direction) => {
        switch (direction?.toUpperCase()) {
            case 'INCREASING':
                return '↑';
            case 'DECREASING':
                return '↓';
            case 'STABLE':
                return '→';
            default:
                return '↔';
        }
    };

    return (
        <div className="page-content pb-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={handleBack}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">{t('reportTitle', { name: agent_name || t('subAgentFallback') })}</h1>
                            <p className="text-gray-600">{t('subtitle', { count: analysisData?.queries_executed || 0 })}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Execution Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-primary-700 mb-1">{t('queriesExecuted')}</p>
                            <p className="text-3xl font-bold text-primary-900">{queries_executed}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <BarChart3 className="w-6 h-6 text-primary-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-700 mb-1">{t('successful')}</p>
                            <p className="text-3xl font-bold text-green-900">{successful_queries}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-red-700 mb-1">{t('failed')}</p>
                            <p className="text-3xl font-bold text-red-900">{failed_queries}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-primary-700 mb-1">{t('insightsAnalyzed')}</p>
                            <p className="text-3xl font-bold text-primary-900">{insights_executed}</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Lightbulb className="w-6 h-6 text-primary-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Parameters Button - Icon below execution summary */}
            {flattenedParams && Object.keys(flattenedParams).length > 0 && (
                <div className="flex justify-end mb-4">
                    <button
                        onClick={() => setShowParamsModal(true)}
                        className="p-2.5 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors shadow-sm group"
                        title={t('viewInputParameters')}
                    >
                        <Info className="w-5 h-5 text-primary-600 group-hover:text-primary-700" />
                    </button>
                </div>
            )}

            {/* Executive Summary */}
            {comprehensive_analysis?.executive_summary && (
                <div className="bg-gradient-to-r from-primary-50 to-primary-50 border border-primary-200 rounded-xl p-6 mb-6">
                    <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-primary-900 mb-3">{t('executiveSummary')}</h2>
                            <p className="text-primary-800 text-base leading-relaxed">
                                {comprehensive_analysis.executive_summary}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Parameters Modal */}
            <ParametersModal
                isOpen={showParamsModal}
                onClose={() => setShowParamsModal(false)}
                parameters={flattenedParams}
            />

            {/* Key Metrics Summary */}
            {comprehensive_analysis?.key_metrics_summary && Object.keys(comprehensive_analysis.key_metrics_summary).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <BarChart3 className="w-6 h-6 text-primary-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('keyMetrics')}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {Object.entries(comprehensive_analysis.key_metrics_summary).map(([key, value]) => (
                            <div key={key} className="bg-gray-50 rounded-lg p-4 text-center">
                                <p className="text-xs text-gray-600 mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                                <p className="text-lg font-bold text-gray-900">{value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Detailed Findings */}
            {comprehensive_analysis?.detailed_findings && comprehensive_analysis.detailed_findings.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <Target className="w-6 h-6 text-primary-600" />
                            <h2 className="text-xl font-bold text-gray-900">{t('detailedFindings')}</h2>
                        </div>
                        <button
                            onClick={() => toggleSection('findings')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {isExpanded('findings') ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                    </div>
                    {isExpanded('findings') && (
                        <div className="space-y-4">
                            {comprehensive_analysis.detailed_findings.map((finding, idx) => (
                                <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900">{finding.title}</h3>
                                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getImpactBadge(finding.impact)}`}>
                                            {finding.impact}
                                        </span>
                                    </div>
                                    <p className="text-gray-700 text-sm mb-3">{finding.description}</p>
                                    {finding.supporting_data && Object.keys(finding.supporting_data).length > 0 && (
                                        <div className="bg-gray-50 rounded p-3">
                                            <p className="text-xs font-medium text-gray-600 mb-2">{t('supportingData')}</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                {Object.entries(finding.supporting_data).map(([k, v]) => (
                                                    <div key={k} className="text-sm">
                                                        <span className="text-gray-600 capitalize">{k.replace(/_/g, ' ')}:</span>
                                                        <span className="font-semibold text-gray-900 ml-2">{v}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Cross-Query Insights */}
            {comprehensive_analysis?.cross_query_insights && comprehensive_analysis.cross_query_insights.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <Activity className="w-6 h-6 text-primary-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('crossQueryInsights')}</h2>
                    </div>
                    <div className="space-y-4">
                        {comprehensive_analysis.cross_query_insights.map((insight, idx) => (
                            <div key={idx} className="border-l-4 border-primary-500 pl-4 py-2 bg-primary-50 rounded-r-lg">
                                <p className="text-gray-800 mb-2">{insight.insight}</p>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-600">{t('connected')}</span>
                                    <div className="flex flex-wrap gap-1">
                                        {insight.queries_connected.map((q, i) => (
                                            <span key={i} className="px-2 py-0.5 bg-primary-200 text-primary-800 rounded text-xs">
                                                {q}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-sm text-gray-700 mt-2"><span className="font-medium">{t('significance')}</span> {insight.significance}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Trends Identified */}
            {comprehensive_analysis?.trends_identified && comprehensive_analysis.trends_identified.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('trendsIdentified')}</h2>
                    </div>
                    <div className="space-y-3">
                        {comprehensive_analysis.trends_identified.map((trend, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl font-bold text-green-600">
                                            {getDirectionIcon(trend.direction)}
                                        </span>
                                        <p className="text-gray-800 font-medium">{trend.trend}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3 text-xs mt-2">
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded">{trend.direction}</span>
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded">{trend.magnitude}</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2"><span className="font-medium">{t('evidence')}</span> {trend.evidence}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Anomalies and Outliers */}
            {comprehensive_analysis?.anomalies_and_outliers && comprehensive_analysis.anomalies_and_outliers.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('anomaliesAndOutliers')}</h2>
                    </div>
                    <div className="space-y-4">
                        {comprehensive_analysis.anomalies_and_outliers.map((anomaly, idx) => (
                            <div key={idx} className="border border-red-200 bg-red-50 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <p className="text-gray-900 font-medium">{anomaly.anomaly}</p>
                                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getImpactBadge(anomaly.severity)}`}>
                                        {anomaly.severity}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1"><span className="font-medium">{t('possibleCause')}</span> {anomaly.possible_cause}</p>
                                <p className="text-sm text-gray-700 mt-1"><span className="font-medium">{t('recommendedAction')}</span> {anomaly.recommended_action}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Strategic Recommendations */}
            {comprehensive_analysis?.strategic_recommendations && comprehensive_analysis.strategic_recommendations.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <Target className="w-6 h-6 text-primary-600" />
                            <h2 className="text-xl font-bold text-gray-900">{t('strategicRecommendations')}</h2>
                        </div>
                        <button
                            onClick={() => toggleSection('recommendations')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {isExpanded('recommendations') ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                    </div>
                    {isExpanded('recommendations') && (
                        <div className="space-y-4">
                            {comprehensive_analysis.strategic_recommendations.map((rec, idx) => (
                                <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                    <p className="text-gray-900 font-medium mb-3">{rec.recommendation}</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div>
                                            <span className="text-gray-600">{t('priority')}</span>
                                            <span className={`ml-2 px-2 py-1 rounded ${getImpactBadge(rec.priority)}`}>
                                                {rec.priority}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">{t('expectedImpact')}</span>
                                            <p className="font-medium text-gray-900">{rec.expected_impact}</p>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">{t('complexity')}</span>
                                            <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-800 rounded">
                                                {rec.implementation_complexity}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">{t('timeline')}</span>
                                            <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-800 rounded">
                                                {rec.timeline}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Business Impact Assessment */}
            {comprehensive_analysis?.business_impact_assessment && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <DollarSign className="w-6 h-6 text-green-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('businessImpactAssessment')}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        {comprehensive_analysis.business_impact_assessment.revenue_impact && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-xs text-green-700 font-medium mb-1">{t('revenueImpact')}</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.revenue_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.cost_impact && (
                            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                <p className="text-xs text-primary-700 font-medium mb-1">{t('costImpact')}</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.cost_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.efficiency_impact && (
                            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                <p className="text-xs text-primary-700 font-medium mb-1">{t('efficiencyImpact')}</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.efficiency_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.risk_impact && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p className="text-xs text-yellow-700 font-medium mb-1">{t('riskImpact')}</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.risk_impact}</p>
                            </div>
                        )}
                        {comprehensive_analysis.business_impact_assessment.customer_impact && (
                            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                                <p className="text-xs text-pink-700 font-medium mb-1">{t('customerImpact')}</p>
                                <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.customer_impact}</p>
                            </div>
                        )}
                    </div>
                    {comprehensive_analysis.business_impact_assessment.overall_assessment && (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-700 mb-1">{t('overallAssessment')}</p>
                            <p className="text-gray-900">{comprehensive_analysis.business_impact_assessment.overall_assessment}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Next Steps */}
            {comprehensive_analysis?.next_steps && comprehensive_analysis.next_steps.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <Clock className="w-6 h-6 text-primary-600" />
                        <h2 className="text-xl font-bold text-gray-900">{t('recommendedNextSteps')}</h2>
                    </div>
                    <div className="space-y-3">
                        {comprehensive_analysis.next_steps.map((step, idx) => (
                            <div key={idx} className="flex items-start gap-3 border border-gray-200 rounded-lg p-4">
                                <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                                    {idx + 1}
                                </div>
                                <div className="flex-1">
                                    <p className="text-gray-900 font-medium">{step.action}</p>
                                    <div className="flex gap-3 mt-2 text-xs">
                                        <span className="text-gray-600">
                                            <Users className="w-3 h-3 inline mr-1" />
                                            {t('owner')} {step.owner}
                                        </span>
                                        <span className="text-gray-600">
                                            <Clock className="w-3 h-3 inline mr-1" />
                                            {t('deadline')} {step.deadline}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Data Quality Notes */}
            {comprehensive_analysis?.data_quality_notes && comprehensive_analysis.data_quality_notes.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
                    <div className="flex items-center space-x-2 mb-3">
                        <AlertCircle className="w-6 h-6 text-yellow-600" />
                        <h2 className="text-lg font-bold text-yellow-900">{t('dataQualityNotes')}</h2>
                    </div>
                    <ul className="space-y-2">
                        {comprehensive_analysis.data_quality_notes.map((note, idx) => (
                            <li key={idx} className="text-sm text-yellow-800 flex items-start gap-2">
                                <span className="text-yellow-600 mt-1">•</span>
                                <span>{note}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
